import { jwtDecode } from "jwt-decode";
import { useEffect, useRef } from "react";
import { useRefreshToken } from "../../../pages/users/hooks/useUsers";
import { getAuth, removeAuth, setAuth } from "./AuthHelpers";

export function useTokenRefresher() {
	const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const createMutation = useRefreshToken();

	const startTimer = (currentAuth?) => {
		const authToUse = currentAuth || getAuth();
		const accessToken = authToUse?.access_token || authToUse?.token;
		if (!accessToken) return;

		try {
			const decoded = jwtDecode<{ exp: number }>(accessToken);
			const expiryTime = decoded.exp * 1000;
			const now = Date.now();

			let delay = expiryTime - now - 60_000;
			if (delay < 1000) delay = 1000;
			const _expiryDate = new Date(expiryTime);
			const _seconds = Math.floor(delay / 1000) % 60;
			const _minutes = Math.floor(delay / 1000 / 60) % 60;
			const _hours = Math.floor(delay / 1000 / 60 / 60);

			// console.log(
			//   `%c[TokenRefresher]`,
			//   "color: #4CAF50; font-weight: bold;",
			//   `Next refresh in ${hours}h ${minutes}m ${seconds}s (≈ ${delay} ms).`,
			//   "Token expires at:",
			//   expiryDate.toLocaleString()
			// );

			if (delay <= 0) {
				console.warn("Token already expired. Logging out.");
				removeAuth();
				window.location.href = "/auth/login";
				return;
			}

			if (refreshTimer.current) clearTimeout(refreshTimer.current);

			refreshTimer.current = setTimeout(() => {
				doRefresh();
			}, delay);
		} catch (err) {
			console.error("Failed to decode token", err);
			removeAuth();
			window.location.href = "/auth/login";
		}
	};

	const doRefresh = () => {
		createMutation.mutate(undefined, {
			onSuccess: (response: any) => {
				const newToken = response.data.data.access_token;
				const decoded = jwtDecode<{ exp: number }>(newToken);

				const oldAuth = getAuth();

				const newAuth: any = {
					access_token: newToken,
					user_id: oldAuth?.user_id || 0,
					role: oldAuth?.role || "",
					username: oldAuth?.username || "",
					state_id: (oldAuth as any)?.state_id || 0,
					district_id: (oldAuth as any)?.district_id || 0,
					block_id: (oldAuth as any)?.block_id || 0,
					employee_code: (oldAuth as any)?.employee_code || "",
					name: oldAuth?.name || "",
					expiration: decoded.exp,
				};

				setAuth(newAuth);

				startTimer(newAuth);
			},
			onError: (err: any) => {
				console.error("Token refresh failed:", err);
				removeAuth();
				window.location.href = "/auth/login";
			},
		});
	};

	useEffect(() => {
		const auth = getAuth();
		if (auth?.access_token) {
			startTimer(auth);
		}

		return () => {
			if (refreshTimer.current) {
				clearTimeout(refreshTimer.current);
			}
		};
	}, [startTimer]);

	return null;
}
